"use client";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  AppBar,
  Toolbar,
  Paper,
  Card,
  ThemeProvider,
  createTheme,
  TextField,
} from "@mui/material";
import { Phone, Email, LocationOn, MenuBook } from "@mui/icons-material";
import { useEffect, useState } from "react";
import Aos from "aos";
import "aos/dist/aos.css";
import toast, { Toaster } from "react-hot-toast";

export default function App() {
  const theme = createTheme({
    typography: {
      fontFamily: `Recursive`, // Add font family globally
    },
  });
  const [formData, setFormData] = useState({
    name: "",
    businessName: "",
    contactNumber: "",
    email: "",
  });
  const [loading, setLoading] = useState(false);
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };
  const validateForm = () => {
    const { name, businessName, contactNumber, email } = formData;

    if (!name || !businessName || !contactNumber || !email) {
      toast.error("All fields are required.");
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address.");
      return false;
    }

    // Contact number validation
    const contactRegex = /^[0-9]{10,15}$/; // Accepts 10 to 15 digits
    if (!contactRegex.test(contactNumber)) {
      toast.error("Please enter a valid contact number.");
      return false;
    }

    return true;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(
        "https://email-service-psi-five.vercel.app/send-email-to-aptax",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData), // Send the form data as JSON
        }
      );

      if (response.ok) {
        const result = await response.json();
        console.log("Email sent successfully:", result);
        toast.success("Your email has been sent successfully!");
      } else {
        console.error("Failed to send email:", response.statusText);
        toast.error("Failed to send email. Please try again later.");
      }
    } catch (error) {
      console.error("Error occurred while sending email:", error);
      toast.error("An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    Aos.init({
      duration: 1000, // Animation duration
      easing: "ease-in-out",
      once: true, // Trigger animation only once
    });
  }, []);
  return (
    <ThemeProvider theme={theme}>
      <Toaster />
      <Box sx={{ minHeight: "100vh", fontFamily: "Recursive" }}>
        {/* Header */}
        <AppBar
          position="static"
          sx={{
            bgcolor: "white",
            boxShadow: "none",
          }}
        >
          <Container maxWidth="xl">
            <Toolbar sx={{ justifyContent: "space-between", py: 1 }}>
              {/* Logo */}
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "1.2rem",
                  }}
                >
                  <img
                    src="/logo192.png"
                    width={208}
                    height={160}
                    style={{
                      aspectRatio: 2746 / 2114,
                    }}
                    alt="aptax"
                  ></img>
                </Box>
              </Box>

              {/* Header Buttons */}
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Button
                  variant="outlined"
                  startIcon={<Phone />}
                  sx={{
                    "&:hover": { bgcolor: "black" },
                    backgroundColor: "#35C0E6",
                    fontSize: "15px",
                    fontWeight: 700,
                    boxShadow:
                      "1px 1px 10px 1px rgba(0, 0, 0, 0.3607843137254902) inset",
                    borderStyle: "solid",
                    borderRadius: "10px 10px 10px 10px",
                    textDecoration: "none",
                    textTransform: "none",
                    minHeight: "50px",
                    display: { xs: "none", sm: "flex" },
                    color: "white",
                    paddingX: 4,
                  }}
                >
                  262-375-2649
                </Button>
                <Button
                  variant="contained"
                  startIcon={<MenuBook />}
                  sx={{
                    "&:hover": { bgcolor: "black" },
                    backgroundColor: "#35C0E6",
                    fontSize: "15px",
                    fontWeight: 700,
                    boxShadow:
                      "1px 1px 10px 1px rgba(0, 0, 0, 0.3607843137254902) inset",
                    borderStyle: "solid",
                    borderRadius: "10px 10px 10px 10px",
                    textDecoration: "none",
                    textTransform: "none",
                    minHeight: "50px",
                    paddingX: 4,
                  }}
                >
                  Book a Consultation
                </Button>
              </Box>
            </Toolbar>
          </Container>
        </AppBar>

        {/* Hero Section with Video Background Effect */}
        <Box
          sx={{
            position: "relative",
            minHeight: "80vh",
            // background: "rgba(255,255,255,0.2)",
            display: "flex",
            alignItems: "center",
            color: "white",
            overflow: "hidden",
          }}
        >
          {/* Background Pattern */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: "url(/bg.jpeg?height=800&width=1200)",
              backgroundSize: "cover",
              backgroundPosition: "center",
              opacity: 0.8,
              zIndex: 1,
            }}
          />

          <Container maxWidth="xl" sx={{ position: "relative", zIndex: 2 }}>
            <Grid container spacing={4} alignItems="center">
              <Grid
                size={{
                  xs: 12,
                  lg: 8,
                }}
                data-aos="fade-up"
              >
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: "2.5rem", md: "4rem", lg: "5rem" },
                    fontWeight: 700,
                    mb: 3,
                    lineHeight: 1.2,
                    color: "black",
                  }}
                >
                  Accounting & Taxation Services for Your Business Success
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { xs: "1.5rem", md: "2rem" },
                    mb: 4,
                    opacity: 0.9,
                    fontWeight: 400,
                    color: "black",
                  }}
                >
                  Your Trusted Partner for Accounting and Tax Solutions
                </Typography>

                {/* What We Do Section */}
                <Card
                  sx={{
                    bgcolor: "rgba(0,0,0,0.8)",
                    color: "white",
                    p: 3,
                    mb: 4,
                    backdropFilter: "blur(10px)",
                    boxShadow: "1px 1px 10px 2px #FFFFFF inset",
                    borderRadius: "20px",
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      mb: 2,
                      textAlign: "center",
                      textShadow: "1px 1px 10px #000000",
                    }}
                  >
                    What We Do
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      textAlign: "center",
                      lineHeight: 1.7,
                      textShadow: "1px 1px 10px #000000",
                    }}
                  >
                    At <strong>AP Income Tax and Accounting LLC</strong>, we
                    specialize in providing reliable, efficient, and
                    personalized financial services tailored for small and
                    medium businesses. With a deep commitment to helping you
                    succeed. Our team of experienced professionals offers
                    comprehensive accounting, payroll, tax compliance and
                    advisory services designed to support your business at every
                    stage of growth.
                  </Typography>
                </Card>
              </Grid>

              <Grid
                xs={12}
                lg={2}
                size={{
                  xs: 12,
                  lg: 4,
                }}
                data-aos="fade-down"
              >
                <Card
                  sx={{
                    p: 3,
                    bgcolor: "#35C0E6AD",
                    boxShadow:
                      "1px 1px 10px 2px rgba(255, 255, 255, 0.5) inset",
                    borderRadius: "20px",
                    maxWidth: 400,
                    mx: "auto",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 3,
                      textAlign: "center",
                      textShadow: "1px 1px 10px #000000",
                      color: "#FFFFFF",
                      fontSize: "33px",
                    }}
                  >
                    Contact Us
                  </Typography>
                  <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    {/* Name Field */}
                    <TextField
                      name="name"
                      label="Name"
                      variant="outlined"
                      fullWidth
                      value={formData.name}
                      onChange={handleChange}
                      sx={{
                        backgroundColor: "white",
                        borderRadius: 4,
                      }}
                      InputProps={{
                        sx: {
                          border: "none",
                          outline: "none",
                          "& fieldset": {
                            border: "none",
                          },
                        },
                      }}
                    />

                    {/* Business Name Field */}
                    <TextField
                      name="businessName"
                      label="Business Name"
                      variant="outlined"
                      fullWidth
                      value={formData.businessName}
                      onChange={handleChange}
                      sx={{
                        backgroundColor: "white",
                        borderRadius: 4,
                      }}
                      InputProps={{
                        sx: {
                          border: "none",
                          outline: "none",
                          "& fieldset": {
                            border: "none",
                          },
                        },
                      }}
                    />

                    {/* Contact Number Field */}
                    <TextField
                      name="contactNumber"
                      label="Contact Number"
                      variant="outlined"
                      fullWidth
                      value={formData.contactNumber}
                      onChange={handleChange}
                      sx={{
                        backgroundColor: "white",
                        borderRadius: 4,
                      }}
                      InputProps={{
                        sx: {
                          border: "none",
                          outline: "none",
                          "& fieldset": {
                            border: "none",
                          },
                        },
                      }}
                    />

                    {/* Email Field */}
                    <TextField
                      name="email"
                      label="Email"
                      variant="outlined"
                      fullWidth
                      value={formData.email}
                      onChange={handleChange}
                      sx={{
                        backgroundColor: "white",
                        borderRadius: 4,
                      }}
                      InputProps={{
                        sx: {
                          border: "none",
                          outline: "none",
                          "& fieldset": {
                            border: "none",
                          },
                        },
                      }}
                    />

                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      sx={{
                        "&:hover": { bgcolor: "black", color: "white" },
                        backgroundColor: "white",
                        fontSize: "15px",
                        fontWeight: 700,
                        boxShadow:
                          "1px 1px 10px 1px rgba(0, 0, 0, 0.3607843137254902) inset",
                        borderStyle: "solid",
                        borderRadius: "10px 10px 10px 10px",
                        textDecoration: "none",
                        textTransform: "none",
                        minHeight: "50px",
                        display: { xs: "none", sm: "flex" },
                        transition: "all 0.3s ease",
                        color: "black",
                        paddingX: 4,
                      }}
                      disabled={loading}
                    >
                      Get Started Today
                    </Button>
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* About Us Section */}
        <Box
          sx={{
            backgroundColor: "transparent",
            backgroundImage:
              "radial-gradient(at center center, #0019A269 0%, #FFFFFF 100%)",
          }}
        >
          <Container
            maxWidth="xl"
            sx={{
              py: 8,
            }}
          >
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Typography
                variant="h3"
                sx={{ mb: 2, fontWeight: 700, color: "#1a5ce0" }}
                data-aos="fade-down"
              >
                About Us
              </Typography>
              {/* <Assessment sx={{ fontSize: 60, color: "#1a5ce0", mb: 2 }} /> */}
            </Box>

            <Grid container spacing={6} sx={{ mb: 8 }}>
              <Grid
                xs={12}
                lg={2}
                size={{
                  xs: 12,
                  md: 6,
                }}
                data-aos="fade-left"
              >
                <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
                  <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
                    Andrew Platta
                  </Typography>
                  <Typography variant="h6" sx={{ mb: 3, color: "#1a5ce0" }}>
                    Certified Public Accountant
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                    Andrew Platta, CPA is a skilled professional with extensive
                    experience in tax compliance, corporate controlling, and
                    teaching. His expertise spans both business and individual
                    tax matters, having worked with a diverse range of clients
                    and industries. Andrew brings a depth of insight that
                    supports businesses and individuals navigate their financial
                    futures.
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    Andrew earned his Bachelor of Science in Economics and his
                    Master of Accounting from the University of Wisconsin –
                    Madison. He has taught accounting courses as an adjunct
                    professor and teaching assistant, and has a passion for
                    teaching accounting in an approachable manner.
                  </Typography>
                </Box>
              </Grid>
              <Grid
                xs={12}
                lg={2}
                size={{
                  xs: 12,
                  md: 6,
                }}
                data-aos="fade-right"
              >
                <Box
                  sx={{
                    width: "80%",
                    height: 400,
                    bgcolor: "#f5f5f5",
                    borderRadius: 2,
                    borderTopLeftRadius: 200,
                    borderBottomLeftRadius: 200,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundImage: "url(/andrew.webp?height=400&width=500)",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    position: "relative",
                  }}
                ></Box>
              </Grid>
            </Grid>

            {/* William J Kirchen Section */}
            <Grid container spacing={6} alignItems="center">
              <Grid
                size={{
                  xs: 12,
                  lg: 2,
                }}
                data-aos="fade-left"
              >
                <Box sx={{ textAlign: "center" }}>
                  <Box
                    sx={{
                      width: 200,
                      height: 200,

                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 2,
                      backgroundImage:
                        "url(/william.webp?height=200&width=200)",
                      backgroundSize: "cover",
                    }}
                  ></Box>
                </Box>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 9,
                }}
                data-aos="fade-right"
              >
                <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
                  About
                </Typography>
                <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
                  William J Kirchen
                </Typography>
                <Typography variant="h6" sx={{ mb: 2, color: "#1a5ce0" }}>
                  Certified Public Accountant
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
                  Bill started our firm in 1988 focusing on serving small
                  business clients in the Ozaukee County community. After many
                  years of serving the local community, Bill decided to begin a
                  transition of ownership to Andrew in late 2024 and gradually
                  shift into retirement.
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                  Bill has a Bachelor's in Accounting from the University of
                  Wisconsin – Madison and is an avid Badger fan. In his spare
                  time Bill enjoys visiting his grandchildren.
                </Typography>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Contact Section */}
        <Box sx={{ py: 8, position: "relative" }}>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: "url('/contact.jpg')", // Fixed the image path
              backgroundSize: "cover", // Ensures the image covers the whole area
              backgroundPosition: "center", // Centers the image
              backgroundRepeat: "no-repeat", // Prevents tiling
              backgroundAttachment: "fixed", // Keeps the image fixed during scrolling
              opacity: 0.8, // Slight transparency for better readability
              zIndex: 1,
              backgroundColor: "black",
            }}
          />
          <Container maxWidth="xl" sx={{ position: "relative", zIndex: 2 }}>
            <Typography
              variant="h3"
              align="center"
              sx={{ mb: 2, fontWeight: 700, color: "white" }}
              data-aos="fade-up"
            >
              Contact Us
            </Typography>
            <Typography
              variant="h5"
              align="center"
              sx={{ mb: 6, color: "white" }}
            >
              We're here to make your tax filing effortless and stress-free.
            </Typography>

            {/* Grid Section */}
            <Grid container spacing={4}>
              <Grid
                item
                size={{
                  xs: 12,
                  md: 6,
                }}
                data-aos="fade-up-right"
              >
                <Paper
                  sx={{
                    p: 4,
                    backdropFilter: "blur(10px)",
                    boxShadow: "1px 1px 10px 1px #7393B3 inset",
                    borderRadius: "20px",
                    bgcolor: "rgba(255, 255, 255, 0.5)",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Phone sx={{ mr: 2, color: "#1a5ce0", fontSize: 30 }} />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Phone
                      </Typography>
                      <Typography variant="body1">262-375-2649</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <LocationOn
                      sx={{ mr: 2, color: "#1a5ce0", fontSize: 30 }}
                    />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Address
                      </Typography>
                      <Typography variant="body1">
                        W57 N14280 Doerr Way, Suite 204, Cedarburg, WI 53012
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Email sx={{ mr: 2, color: "#1a5ce0", fontSize: 30 }} />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Email
                      </Typography>
                      <Typography variant="body1">
                        aplatta@aptaxwi.com
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
              <Grid
                item
                size={{
                  xs: 12,
                  md: 5,
                }}
                data-aos="fade-up-left"
              >
                <Paper
                  sx={{
                    p: "20px",
                    borderRadius: 3,
                    height: "100%",
                    backgroundColor: "#1346AFC2",
                    boxShadow:
                      "1px 1px 10px 2px rgba(255, 255, 255, 0.5) inset",
                    gap: 2,
                  }}
                >
                  <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        mb: 3,
                        textAlign: "center",
                        textShadow: "1px 1px 10px #000000",
                        color: "#FFFFFF",
                        fontSize: "33px",
                      }}
                    >
                      Get in Touch Now
                    </Typography>
                    {/* TextField Components */}
                    <TextField
                      name="name"
                      label="Name"
                      variant="outlined"
                      fullWidth
                      value={formData.name}
                      onChange={handleChange}
                      sx={{
                        backgroundColor: "white",
                        borderRadius: 4,
                      }}
                      InputProps={{
                        sx: {
                          border: "none",
                          outline: "none",
                          "& fieldset": {
                            border: "none",
                          },
                        },
                      }}
                    />
                    {/* Additional Fields */}
                    <TextField
                      name="businessName"
                      label="Business Name"
                      variant="outlined"
                      fullWidth
                      value={formData.businessName}
                      onChange={handleChange}
                      sx={{
                        backgroundColor: "white",
                        borderRadius: 4,
                      }}
                      InputProps={{
                        sx: {
                          border: "none",
                          outline: "none",
                          "& fieldset": {
                            border: "none",
                          },
                        },
                      }}
                    />
                    <TextField
                      name="contactNumber"
                      label="Contact Number"
                      variant="outlined"
                      fullWidth
                      value={formData.contactNumber}
                      onChange={handleChange}
                      sx={{
                        backgroundColor: "white",
                        borderRadius: 4,
                      }}
                      InputProps={{
                        sx: {
                          border: "none",
                          outline: "none",
                          "& fieldset": {
                            border: "none",
                          },
                        },
                      }}
                    />
                    <TextField
                      name="email"
                      label="Email"
                      variant="outlined"
                      fullWidth
                      value={formData.email}
                      onChange={handleChange}
                      sx={{
                        backgroundColor: "white",
                        borderRadius: 4,
                      }}
                      InputProps={{
                        sx: {
                          border: "none",
                          outline: "none",
                          "& fieldset": {
                            border: "none",
                          },
                        },
                      }}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      sx={{
                        "&:hover": { bgcolor: "black" },
                        backgroundColor: "white",
                        fontSize: "15px",
                        fontWeight: 700,
                        boxShadow:
                          "1px 1px 10px 1px rgba(0, 0, 0, 0.3607843137254902) inset",
                        borderStyle: "solid",
                        borderRadius: "10px 10px 10px 10px",
                        textDecoration: "none",
                        textTransform: "none",
                        minHeight: "50px",
                        display: { xs: "none", sm: "flex" },
                        color: "black",
                        paddingX: 4,
                      }}
                      disabled={loading}
                    >
                      Contact Us Today
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Services Sections */}
        <Container maxWidth="xl" sx={{ py: 8 }}>
          {/* Comprehensive Accounting Services */}
          <Grid container spacing={6} sx={{ mb: 8 }} alignItems="center">
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
              data-aos="fade-up-left"
            >
              <Box
                sx={{
                  width: "80%",
                  height: 300,
                  bgcolor: "#f5f5f5",
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundImage: "url(/comp.jpg?height=300&width=500)",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  borderTopRightRadius: "200px",
                  borderBottomRightRadius: "200px",
                }}
              ></Box>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 5,
              }}
              data-aos="fade-up-right"
            >
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
                Comprehensive Accounting Services
              </Typography>
              <Typography
                variant="body1"
                sx={{ lineHeight: 1.7, fontSize: "1.1rem" }}
              >
                Keep your finances in perfect order with our expert accounting
                solutions. From meticulous bookkeeping to detailed financial
                reporting, we provide insights that help your business thrive.
              </Typography>
            </Grid>
          </Grid>

          {/* Payroll Management */}
          <Grid container spacing={6} sx={{ mb: 8 }} alignItems="center">
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
              order={{ xs: 2, md: 1 }}
              data-aos="fade-up-left"
            >
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
                Payroll Management
              </Typography>
              <Typography
                variant="body1"
                sx={{ lineHeight: 1.7, fontSize: "1.1rem" }}
              >
                Effortless payroll solutions tailored to your needs. We handle
                salaries, deductions, and compliance, ensuring your employees
                are paid accurately and on time.
              </Typography>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
              order={{ xs: 1, md: 2 }}
              data-aos="fade-up-right"
            >
              <Box
                sx={{
                  width: "80%",
                  height: 300,
                  bgcolor: "#f5f5f5",
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundImage: "url(/payroll.webp?height=300&width=500)",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  borderTopRightRadius: "200px",
                  borderBottomRightRadius: "200px",
                }}
              ></Box>
            </Grid>
          </Grid>

          {/* Tax Compliance Made Easy */}
          <Grid container spacing={6} sx={{ mb: 8 }} alignItems="center">
            <Grid
              item
              size={{
                xs: 12,
                md: 6,
              }}
              data-aos="fade-up-left"
            >
              <Box
                sx={{
                  width: "80%",
                  height: 300,
                  bgcolor: "#f5f5f5",
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundImage: "url(/tax.jpg?height=300&width=500)",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  borderTopRightRadius: "200px",
                  borderBottomRightRadius: "200px",
                }}
              ></Box>
            </Grid>
            <Grid
              item
              size={{
                xs: 12,
                md: 5,
              }}
              data-aos="fade-up-right"
            >
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
                Tax Compliance Made Easy
              </Typography>
              <Typography
                variant="body1"
                sx={{ lineHeight: 1.7, fontSize: "1.1rem" }}
              >
                Stay on top of tax regulations with our comprehensive tax
                compliance services. We ensure accurate calculations, timely
                filings, and adherence to the latest legal requirements.
              </Typography>
            </Grid>
          </Grid>

          {/* Advisory Services for Growth */}
          <Grid container spacing={6} alignItems="center">
            <Grid
              item
              size={{
                xs: 12,
                md: 6,
              }}
              order={{ xs: 2, md: 1 }}
              data-aos="fade-up-left"
            >
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
                Advisory Services for Growth
              </Typography>
              <Typography
                variant="body1"
                sx={{ lineHeight: 1.7, fontSize: "1.1rem" }}
              >
                Empower your business decisions with our professional advisory
                services. From strategic planning to financial analysis, we
                guide you toward sustainable success.
              </Typography>
            </Grid>
            <Grid
              item
              size={{
                xs: 12,
                md: 6,
              }}
              order={{ xs: 1, md: 2 }}
              data-aos="fade-up-right"
            >
              <Box
                sx={{
                  width: "80%",
                  height: 300,
                  bgcolor: "#f5f5f5",
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundImage: "url(/advisory.jpg?height=300&width=500)",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  borderTopRightRadius: "200px",
                  borderBottomRightRadius: "200px",
                }}
              ></Box>
            </Grid>
          </Grid>
        </Container>

        {/* Find Us Section */}
        <Box sx={{ bgcolor: "#f5f5f5", py: 8 }}>
          <Container maxWidth="xl">
            <Typography
              variant="h3"
              align="center"
              sx={{ mb: 4, fontWeight: 700 }}
              data-aos="fade-up"
            >
              Find Us
            </Typography>
            <Box
              sx={{
                width: "100%",
                height: 400,
                bgcolor: "#e0e0e0",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundImage: "url(/placeholder.svg?height=400&width=1000)",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
              data-aos="fade-down"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d371810.93324828264!2d-87.980486!3d43.277133!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8804e50370557e63%3A0x3aab2dc2dded17d1!2sW57N14280%20Doerr%20Way%20%23204%2C%20Cedarburg%2C%20WI%2053012!5e0!3m2!1sen!2sus!4v1750103966728!5m2!1sen!2sus"
                width={"100%"}
                height="400"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                title="location"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </Box>
          </Container>
        </Box>

        {/* Footer */}
        <Box sx={{ bgcolor: "black", color: "white", py: 6 }}>
          <Container maxWidth="xl">
            <Grid container spacing={4}>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
                data-aos="fade-up-left"
              >
                <Box sx={{ mb: 4 }}>
                  <Box
                    sx={{
                      width: 200,
                      height: 160,
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 3,
                    }}
                  >
                    <img
                      src="/logo-white.png"
                      width={208}
                      height={160}
                      style={{
                        aspectRatio: 2746 / 2114,
                      }}
                      alt="aptax"
                    ></img>
                  </Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Your Trusted Partner for Accounting and Tax Solutions
                  </Typography>
                </Box>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
                data-aos="fade-up-right"
              >
                <Typography
                  variant="h5"
                  sx={{ mb: 3, fontWeight: 700, color: "#4fc3f7" }}
                >
                  Get In Touch
                </Typography>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Contact Details
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Phone: 262-375-2649
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Email: aplatta@aptaxwi.com
                </Typography>
                <Typography variant="body1">
                  Address: W57 N14280 Doerr Way, Suite 204, Cedarburg, WI 53012
                </Typography>
              </Grid>
            </Grid>
            <Box
              sx={{
                textAlign: "center",
                mt: 4,
                pt: 4,
                borderTop: "1px solid #444",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "white",
                }}
              >
                ©2024 Cover & Rossiter. All Rights Reserved.
              </Typography>
            </Box>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
